import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { contactService, Contact } from "@/services/contactService";
import {
  ConversationStatus,
  defaultVisibleStatuses,
} from "@/types/conversation";

// Types for the context
interface ContactsState {
  contacts: Contact[];
  isLoading: boolean;
  error: Error | null;
  isInitialized: boolean;
  lastFetchTime: number | null;

  // Filter states - persisted across navigation
  searchTerm: string;
  userTypeFilter: "all" | "leads" | "clients";
  sentimentFilter: string;
  showHighPriority: boolean;
  statusFilter: ConversationStatus[];
  sortField: string | null;
  sortDirection: "asc" | "desc" | null;

  // Cache for filtered results
  filteredResultsCache: Map<string, Contact[]>;

  // Contact details caching
  selectedContactId: string | null;
  contactDetailsCache: Map<string, { formFields: any[]; timestamp: number }>;
}

type ContactsAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_CONTACTS"; payload: Contact[] }
  | { type: "SET_ERROR"; payload: Error | null }
  | { type: "SET_INITIALIZED"; payload: boolean }
  | { type: "SET_LAST_FETCH_TIME"; payload: number }
  | { type: "SET_SEARCH_TERM"; payload: string }
  | { type: "SET_USER_TYPE_FILTER"; payload: "all" | "leads" | "clients" }
  | { type: "SET_SENTIMENT_FILTER"; payload: string }
  | { type: "SET_SHOW_HIGH_PRIORITY"; payload: boolean }
  | { type: "SET_STATUS_FILTER"; payload: ConversationStatus[] }
  | { type: "SET_SORT_FIELD"; payload: string | null }
  | { type: "SET_SORT_DIRECTION"; payload: "asc" | "desc" | null }
  | {
      type: "UPDATE_CONTACT";
      payload: { conversationId: string; updates: Partial<Contact> };
    }
  | {
      type: "CACHE_FILTERED_RESULTS";
      payload: { key: string; results: Contact[] };
    }
  | { type: "CLEAR_CACHE" }
  | { type: "SET_SELECTED_CONTACT"; payload: string | null }
  | {
      type: "CACHE_CONTACT_DETAILS";
      payload: { contactId: string; formFields: any[] };
    };

interface ContactsContextType {
  // State
  contacts: Contact[];
  isLoading: boolean;
  error: Error | null;
  isInitialized: boolean;
  selectedContactId: string | null;

  // Filter states
  searchTerm: string;
  userTypeFilter: "all" | "leads" | "clients";
  sentimentFilter: string;
  showHighPriority: boolean;
  statusFilter: ConversationStatus[];
  sortField: string | null;
  sortDirection: "asc" | "desc" | null;

  // Actions
  fetchContacts: (force?: boolean) => Promise<void>;
  refreshContacts: () => Promise<void>;
  updateContact: (conversationId: string, updates: Partial<Contact>) => void;

  // Filter actions
  setSearchTerm: (term: string) => void;
  setUserTypeFilter: (filter: "all" | "leads" | "clients") => void;
  setSentimentFilter: (filter: string) => void;
  setShowHighPriority: (show: boolean) => void;
  setStatusFilter: (statuses: ConversationStatus[]) => void;
  setSortField: (field: string | null) => void;
  setSortDirection: (direction: "asc" | "desc" | null) => void;

  // Contact details
  selectContact: (contactId: string | null) => void;
  getContactFormFields: (contactId: string) => any[] | null;

  // Filtered results
  getFilteredContacts: () => Contact[];
}

const ContactsContext = createContext<ContactsContextType | undefined>(
  undefined,
);

// Initial state
const initialState: ContactsState = {
  contacts: [],
  isLoading: false,
  error: null,
  isInitialized: false,
  lastFetchTime: null,

  // Filter defaults
  searchTerm: "",
  userTypeFilter: "all",
  sentimentFilter: "all",
  showHighPriority: false,
  statusFilter: defaultVisibleStatuses,
  sortField: null,
  sortDirection: null,

  filteredResultsCache: new Map(),

  // Contact details caching
  selectedContactId: null,
  contactDetailsCache: new Map(),
};

// Reducer
function contactsReducer(
  state: ContactsState,
  action: ContactsAction,
): ContactsState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };

    case "SET_CONTACTS":
      return {
        ...state,
        contacts: action.payload,
        filteredResultsCache: new Map(), // Clear cache when contacts change
      };

    case "SET_ERROR":
      return { ...state, error: action.payload };

    case "SET_INITIALIZED":
      return { ...state, isInitialized: action.payload };

    case "SET_LAST_FETCH_TIME":
      return { ...state, lastFetchTime: action.payload };

    case "SET_SEARCH_TERM":
      return {
        ...state,
        searchTerm: action.payload,
        filteredResultsCache: new Map(), // Clear cache when filters change
      };

    case "SET_USER_TYPE_FILTER":
      return {
        ...state,
        userTypeFilter: action.payload,
        filteredResultsCache: new Map(),
      };

    case "SET_SENTIMENT_FILTER":
      return {
        ...state,
        sentimentFilter: action.payload,
        filteredResultsCache: new Map(),
      };

    case "SET_SHOW_HIGH_PRIORITY":
      return {
        ...state,
        showHighPriority: action.payload,
        filteredResultsCache: new Map(),
      };

    case "SET_STATUS_FILTER":
      return {
        ...state,
        statusFilter: action.payload,
        filteredResultsCache: new Map(),
      };

    case "SET_SORT_FIELD":
      return {
        ...state,
        sortField: action.payload,
        filteredResultsCache: new Map(),
      };

    case "SET_SORT_DIRECTION":
      return {
        ...state,
        sortDirection: action.payload,
        filteredResultsCache: new Map(),
      };

    case "UPDATE_CONTACT":
      return {
        ...state,
        contacts: state.contacts.map((contact) =>
          contact.conversation_id === action.payload.conversationId
            ? { ...contact, ...action.payload.updates }
            : contact,
        ),
        filteredResultsCache: new Map(), // Clear cache when data changes
      };

    case "CACHE_FILTERED_RESULTS":
      const newCache = new Map(state.filteredResultsCache);
      newCache.set(action.payload.key, action.payload.results);

      // Limit cache size
      if (newCache.size > 10) {
        const firstKey = newCache.keys().next().value;
        newCache.delete(firstKey);
      }

      return { ...state, filteredResultsCache: newCache };

    case "CLEAR_CACHE":
      return { ...state, filteredResultsCache: new Map() };

    case "SET_SELECTED_CONTACT":
      return { ...state, selectedContactId: action.payload };

    case "CACHE_CONTACT_DETAILS":
      const contactDetailsCache = new Map(state.contactDetailsCache);
      contactDetailsCache.set(action.payload.contactId, {
        formFields: action.payload.formFields,
        timestamp: Date.now(),
      });
      return { ...state, contactDetailsCache };

    default:
      return state;
  }
}

// Provider component
interface ContactsProviderProps {
  children: ReactNode;
}

export function ContactsProvider({ children }: ContactsProviderProps) {
  const [state, dispatch] = useReducer(contactsReducer, initialState);

  // Cache TTL (30 minutes)
  const CACHE_TTL = 30 * 60 * 1000;

  // Check if we need to fetch data
  const shouldFetchContacts = useCallback(() => {
    if (!state.isInitialized) return true;
    if (!state.lastFetchTime) return true;
    return Date.now() - state.lastFetchTime > CACHE_TTL;
  }, [state.isInitialized, state.lastFetchTime]);

  // Fetch contacts
  const fetchContacts = useCallback(
    async (force: boolean = false) => {
      if (!force && !shouldFetchContacts()) {
        console.log("📦 Using cached contacts data");
        return;
      }

      console.log("🔄 Fetching contacts from API");
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });

      try {
        const { data, error } = await contactService.getContacts();

        if (error) throw error;

        dispatch({ type: "SET_CONTACTS", payload: data || [] });
        dispatch({ type: "SET_LAST_FETCH_TIME", payload: Date.now() });
        dispatch({ type: "SET_INITIALIZED", payload: true });

        console.log("✅ Contacts loaded successfully:", data?.length || 0);
      } catch (error) {
        console.error("❌ Error fetching contacts:", error);
        dispatch({ type: "SET_ERROR", payload: error as Error });
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [shouldFetchContacts],
  );

  // Refresh contacts (force fetch)
  const refreshContacts = useCallback(async () => {
    await fetchContacts(true);
  }, [fetchContacts]);

  // Update a specific contact
  const updateContact = useCallback(
    (conversationId: string, updates: Partial<Contact>) => {
      dispatch({
        type: "UPDATE_CONTACT",
        payload: { conversationId, updates },
      });
    },
    [],
  );

  // Filter action creators
  const setSearchTerm = useCallback((term: string) => {
    dispatch({ type: "SET_SEARCH_TERM", payload: term });
  }, []);

  const setUserTypeFilter = useCallback(
    (filter: "all" | "leads" | "clients") => {
      dispatch({ type: "SET_USER_TYPE_FILTER", payload: filter });
    },
    [],
  );

  const setSentimentFilter = useCallback((filter: string) => {
    dispatch({ type: "SET_SENTIMENT_FILTER", payload: filter });
  }, []);

  const setShowHighPriority = useCallback((show: boolean) => {
    dispatch({ type: "SET_SHOW_HIGH_PRIORITY", payload: show });
  }, []);

  const setStatusFilter = useCallback((statuses: ConversationStatus[]) => {
    dispatch({ type: "SET_STATUS_FILTER", payload: statuses });
  }, []);

  const setSortField = useCallback((field: string | null) => {
    dispatch({ type: "SET_SORT_FIELD", payload: field });
  }, []);

  const setSortDirection = useCallback((direction: "asc" | "desc" | null) => {
    dispatch({ type: "SET_SORT_DIRECTION", payload: direction });
  }, []);

  // Get filtered contacts with caching
  const getFilteredContacts = useCallback(() => {
    const cacheKey = JSON.stringify({
      searchTerm: state.searchTerm,
      userTypeFilter: state.userTypeFilter,
      sentimentFilter: state.sentimentFilter,
      showHighPriority: state.showHighPriority,
      statusFilter: state.statusFilter,
      sortField: state.sortField,
      sortDirection: state.sortDirection,
      contactsLength: state.contacts.length,
    });

    // Check cache first
    if (state.filteredResultsCache.has(cacheKey)) {
      console.log("📦 Using cached filtered contacts");
      return state.filteredResultsCache.get(cacheKey)!;
    }

    console.log("🔄 Computing filtered contacts");

    let filtered = contactService.filterContacts(
      state.contacts,
      state.searchTerm,
    );

    // Apply filters
    if (state.userTypeFilter === "leads") {
      filtered = filtered.filter((contact) => contact.user_type === "lead");
    } else if (state.userTypeFilter === "clients") {
      filtered = filtered.filter((contact) => contact.user_type === "client");
    }

    if (state.sentimentFilter !== "all") {
      filtered = filtered.filter(
        (contact) => contact.sentiment === state.sentimentFilter,
      );
    }

    if (state.showHighPriority) {
      filtered = filtered.filter(
        (contact) =>
          contact.priority === "urgent" || contact.priority === "high",
      );
    }

    if (state.statusFilter.length > 0) {
      filtered = filtered.filter((contact) => {
        const contactStatus = contact.status || "open";
        return state.statusFilter.includes(contactStatus as ConversationStatus);
      });
    }

    // Apply sorting
    if (state.sortField && state.sortDirection) {
      filtered.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (state.sortField) {
          case "date":
            aValue = new Date(a.conversation_date).getTime();
            bValue = new Date(b.conversation_date).getTime();
            break;
          case "type":
            aValue = a.user_type || "unknown";
            bValue = b.user_type || "unknown";
            break;
          case "intent":
            aValue = a.intent || "";
            bValue = b.intent || "";
            break;
          case "sentiment":
            aValue = a.sentiment || "unknown";
            bValue = b.sentiment || "unknown";
            break;
          case "priority":
            const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
            aValue =
              priorityOrder[a.priority as keyof typeof priorityOrder] || -1;
            bValue =
              priorityOrder[b.priority as keyof typeof priorityOrder] || -1;
            break;
          case "status":
            const statusOrder = {
              action_required: 5,
              in_progress: 4,
              open: 3,
              closed: 2,
              archived: 1,
            };
            aValue = statusOrder[a.status as keyof typeof statusOrder] || -1;
            bValue = statusOrder[b.status as keyof typeof statusOrder] || -1;
            break;
          default:
            return 0;
        }

        if (state.sortDirection === "asc") {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });
    }

    // Cache the result
    dispatch({
      type: "CACHE_FILTERED_RESULTS",
      payload: { key: cacheKey, results: filtered },
    });

    console.log("✅ Filtered contacts computed and cached:", filtered.length);
    return filtered;
  }, [
    state.contacts,
    state.searchTerm,
    state.userTypeFilter,
    state.sentimentFilter,
    state.showHighPriority,
    state.statusFilter,
    state.sortField,
    state.sortDirection,
    state.filteredResultsCache,
  ]);

  // Auto-fetch on mount
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Context value
  const value: ContactsContextType = {
    // State
    contacts: state.contacts,
    isLoading: state.isLoading,
    error: state.error,
    isInitialized: state.isInitialized,
    selectedContactId: state.selectedContactId,

    // Filter states
    searchTerm: state.searchTerm,
    userTypeFilter: state.userTypeFilter,
    sentimentFilter: state.sentimentFilter,
    showHighPriority: state.showHighPriority,
    statusFilter: state.statusFilter,
    sortField: state.sortField,
    sortDirection: state.sortDirection,

    // Actions
    fetchContacts,
    refreshContacts,
    updateContact,

    // Filter actions
    setSearchTerm,
    setUserTypeFilter,
    setSentimentFilter,
    setShowHighPriority,
    setStatusFilter,
    setSortField,
    setSortDirection,

    // Contact details
    selectContact: (contactId) =>
      dispatch({ type: "SET_SELECTED_CONTACT", payload: contactId }),
    getContactFormFields: (contactId) => {
      const cached = state.contactDetailsCache.get(contactId);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        console.log(`📦 Using cached contact details for ${contactId}`);
        return cached.formFields;
      }
      console.log(`🔄 Fetching contact details for ${contactId}`);
      return null;
    },

    // Filtered results
    getFilteredContacts,
  };

  return (
    <ContactsContext.Provider value={value}>
      {children}
    </ContactsContext.Provider>
  );
}

// Hook to use the context
export function useContacts() {
  const context = useContext(ContactsContext);
  if (context === undefined) {
    throw new Error("useContacts must be used within a ContactsProvider");
  }
  return context;
}
